import {
  type CivilDate,
  civilDateToInputValue,
  compareCivilDate,
  isValidCivilDate,
  parseDateInputValue,
} from '~/models/civilDate'
import {
  INVALID,
  defineFilterCodec,
  field,
  optional,
} from '~/models/filterCodec'

/**
 * 清算画面のフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) は「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を保持し、他画面への遷移後・再訪時に前回の選択状態を
 * 復元する」と定める (保持方式は EN 判断範囲)。 他の画面と同じく localStorage に持つ
 * (`~/hooks/usePersistedFilters`)。
 */
export const JEPX_SETTLEMENT_FILTERS_STORAGE_KEY =
  'ebalance-jepx-settlement-filters'

/**
 * 清算画面のフィルタバー state (in-memory 形状)。 画面遷移・再訪をまたいで保持する対象。
 *
 * 保持するのは**清算日の期間 (開始日・終了日、 各々任意)** だけ。 「期間をクリア」で
 * 「すべて」(両方 `undefined`) に戻した状態も保持する。
 *
 * ## 保持しないもの
 *
 * - **市場タブ (スポット / 時間前)** — フィルタバーの外の別軸で、 フィルタではなく
 *   表示面の切替。 同じ形の既存画面もタブは保持していない
 *   (`pages/ProcurementPlanList/models/persistedFilters.ts` の計画期間タブ)。 再訪時は
 *   常にスポットから始まる。
 */
export type JepxSettlementFilters = {
  /** 清算日の期間の開始日 (含む)。 `undefined` = 下限なし。 */
  from: CivilDate | undefined
  /** 清算日の期間の終了日 (含む)。 `undefined` = 上限なし。 */
  to: CivilDate | undefined
}

/** フィルタ既定値 = 期間指定なし (すべて)。 時計に依存しない。 */
export function defaultJepxSettlementFilters(): JepxSettlementFilters {
  return { from: undefined, to: undefined }
}

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const civilDate = field<CivilDate>((wire) => {
  if (typeof wire !== 'string' || !YMD_PATTERN.test(wire)) return INVALID
  const parsed = parseDateInputValue(wire)
  return parsed === undefined || !isValidCivilDate(parsed) ? INVALID : parsed
}, civilDateToInputValue)

/**
 * 保存形式は `{ from: 'YYYY-MM-DD' | null, to: 'YYYY-MM-DD' | null }`。
 *
 * 壊れた値は黙って**期間なし (すべて) の既定**へ落とす (deploy skew / 手書き改変で
 * 取得を止めない・全画面エラーにしない)。 JSON でない / オブジェクトでない / 形式違い
 * (`YYYY-MM-DD` でない・キーが欠けている) / 暦に無い日付 / **開始日 > 終了日**
 * (backend は InvalidArgument にする組み合わせで、 画面も作らせない:
 * `components/PeriodFilter.tsx`) のどれも既定へ落とす。 片方だけ壊れている時も、
 * 残りの片方だけを復元せず全体を既定へ戻す (保存していない期間を画面が作らないため)。
 */
export const jepxSettlementFiltersCodec =
  defineFilterCodec<JepxSettlementFilters>({
    defaults: defaultJepxSettlementFilters,
    fields: {
      from: optional(civilDate, { wire: 'null' }),
      to: optional(civilDate, { wire: 'null' }),
    },
    onInvalid: 'all',
    validate: ({ from, to }) =>
      from === undefined || to === undefined || compareCivilDate(from, to) <= 0,
  })
